import React, { useCallback, useMemo, useState, useEffect } from 'react';
import type {
  ViewState,
  SpecialEvent,
  DayOfWeek,
  SearchParams,
  SubscriptionPlan,
  VenueIdentity,
} from './types';

import LandingPage from './components/LandingPage';
import ResultsPage from './components/ResultsPage';
import MerchantPortal from './components/MerchantPortal';
import SpecialEditor from './components/SpecialEditor';
import MerchantAuth from './components/MerchantAuth';
import ThankYouSimple from "./components/ThankYouSimple";
import VenuePage from './components/VenuePage';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';

import { useAuth } from './services/auth/AuthContext';
import { supabase } from './services/supabaseClient';

const INITIAL_IDENTITY: VenueIdentity = {
  venueName: '',
  address: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  website: '',
  facebook: '',
  instagram: '',
  xHandle: '',
  logoUrl: '',
  mainImageUrl: '',
  galleryImages: [],
  isVerified: false,
  is_verified: false,
};

const INITIAL_SEARCH: SearchParams = {
  date: '',
  time: '',
  location: '',
  selectedCities: [],
};

// --- helpers to map DB rows -> UI types ---

const DAY_NAMES: Record<number, DayOfWeek> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

function activeDaysToNames(activeDays: any): DayOfWeek[] {
  if (!Array.isArray(activeDays)) return [];
  return activeDays
    .map((d) => {
      const n = typeof d === "string" ? parseInt(d, 10) : d;
      const normalized = n === 7 ? 0 : n; // legacy support
      return DAY_NAMES[normalized];
    })
    .filter(Boolean);
}

function hhmm(val: any, fallback: string) {
  if (!val) return fallback;
  const s = String(val);
  // "14:30:00" -> "14:30"
  if (s.length >= 5) return s.slice(0, 5);
  return fallback;
}

function isVenueVerified(value: any) {
  return value === true;
}

function compareVerifiedFirst(a: Pick<SpecialEvent, 'isVerified' | 'is_verified'>, b: Pick<SpecialEvent, 'isVerified' | 'is_verified'>) {
  const verifiedDiff = Number(isVenueVerified((b as any)?.isVerified ?? (b as any)?.is_verified)) - Number(isVenueVerified((a as any)?.isVerified ?? (a as any)?.is_verified));
  return verifiedDiff;
}

function toSafeArray<T = any>(value: any): T[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
  }
  return [];
}

function normalizeDealCategory(value: any): 'Food' | 'Beer' | 'Wine' | 'Cocktail' {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'food') return 'Food';
  if (normalized === 'wine') return 'Wine';
  if (normalized === 'cocktail') return 'Cocktail';
  return 'Beer';
}

function normalizeDeal(rawDeal: any, index: number) {
  const item = String(
    rawDeal?.item ?? rawDeal?.name ?? rawDeal?.title ?? rawDeal?.label ?? rawDeal?.description ?? ''
  ).trim();
  const price = String(rawDeal?.price ?? rawDeal?.value ?? rawDeal?.amount ?? '').trim();

  return {
    id: String(rawDeal?.id ?? `deal-${index}`),
    item,
    price,
    category: normalizeDealCategory(rawDeal?.category),
    isFeatured: Boolean(rawDeal?.isFeatured),
    isGroupDeal: Boolean(rawDeal?.isGroupDeal),
  };
}

function toSafeDeals(value: any) {
  return toSafeArray(value)
    .map((deal, index) => normalizeDeal(deal, index))
    .filter((deal) => deal.item.length > 0 || deal.price.length > 0);
}

function toFiniteNumber(value: any): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export default function App() {
  const { user, loading: authLoading, signOut } = useAuth();

  const [merchantInitialTab, setMerchantInitialTab] =
    useState<'specials' | 'identity' | 'flash'>('specials');

  const [view, setView] = useState<ViewState>('landing');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  // ✅ Split public browsing vs merchant editing
  const [publicEvents, setPublicEvents] = useState<SpecialEvent[]>([]);
  const [merchantEvents, setMerchantEvents] = useState<SpecialEvent[]>([]);

  const [searchParams, setSearchParams] = useState<SearchParams>(INITIAL_SEARCH);
  const [selectedVenue, setSelectedVenue] = useState<SpecialEvent | null>(null);

  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [venueUuid, setVenueUuid] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [listingStatus, setListingStatus] = useState<string | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan>('partner' as SubscriptionPlan);

  const [venueIdentity, setVenueIdentity] = useState<VenueIdentity>(INITIAL_IDENTITY);

  const [portalInitialTab, setPortalInitialTab] = useState<string>('specials');
  const [editingSpecial, setEditingSpecial] = useState<SpecialEvent | null>(null);

  const isLoggedIn = useMemo(() => !!currentUserEmail, [currentUserEmail]);
  const [routeReady, setRouteReady] = useState(false);

 const navigateTo = useCallback((v: ViewState) => setView(v), []);
  const navigateToAuth = useCallback((mode: 'login' | 'register') => {
    setAuthMode(mode);
    setView('merchant-auth');
  }, []);

  const navigateToPreviewSignup = useCallback((venueName: string) => {
    const params = new URLSearchParams({ mode: 'register' });
    const normalizedVenueName = venueName.trim();

    if (normalizedVenueName) {
      params.set('previewVenue', normalizedVenueName);
    }

    window.location.hash = `#/merchant-auth?${params.toString()}`;
  }, []);
  
  const applyRouteFromLocation = useCallback(() => {
    const rawPath = window.location.pathname;
    const rawHash = window.location.hash;
    const isAppHashRoute = rawHash.startsWith('#/');
    const routeSource = isAppHashRoute ? rawHash : (rawPath || '');

    const cleaned = routeSource.replace(/^#/, '').replace(/^\//, '').trim();
    const [pathKey, queryString = ''] = cleaned.split('?');
    const params = new URLSearchParams(queryString);

    if (pathKey === 'merchant-auth') {
      const mode = params.get('mode');
      if (mode === 'register' || mode === 'login') {
        setAuthMode(mode);
      }
      setView('merchant-auth');
    } else if (pathKey === 'venue-hub') {
      const tab = params.get('tab');
      if (tab === 'identity' || tab === 'specials' || tab === 'flash') {
        setMerchantInitialTab(tab);
      }
      setView('merchant-portal');
    } else if (pathKey === 'special-editor') {
      setView('special-editor');
    } else if (pathKey === 'venue' || pathKey === 'venue-page') {
      setView('venue-page');
    } else if (pathKey === 'thank-you') {
      setView('thank-you');
    } else if (pathKey === 'privacy-policy') {
       setView('privacy-policy');
    } else if (pathKey === 'terms-of-service') {
      setView('terms-of-service');
    } else if (pathKey === 'results') {
      setView('results');
    } else {
      setView('landing'); 
    }

    setRouteReady(true);
  }, []);

  useEffect(() => {
    applyRouteFromLocation();
    window.addEventListener('hashchange', applyRouteFromLocation);
    return () => window.removeEventListener('hashchange', applyRouteFromLocation);
  }, [applyRouteFromLocation]);

  // Restore currentUserEmail from Supabase session (e.g. after Stripe redirect)
  useEffect(() => {
    if (authLoading) return;
    if (user?.email && !currentUserEmail) {
      setCurrentUserEmail(user.email);
    }
  }, [user, authLoading, currentUserEmail]);

  // -----------------------------
  // Helper: Map DB row -> SpecialEvent (public)
  // -----------------------------
  const mapPublicRowToEvent = useCallback((row: any, venue: any): SpecialEvent => {
    const start = hhmm(row?.start_time, '16:00');
    const end = hhmm(row?.end_time, '19:00');
    const lat = toFiniteNumber(venue?.latitude ?? row?.latitude ?? row?.lat);
    const lng = toFiniteNumber(venue?.longitude ?? row?.longitude ?? row?.lng);

    return {
      id: row.id,
      name: row.title,
      specialType: row.title,
      startTime: start,
      endTime: end,
      days: activeDaysToNames(row?.active_days),
      deals: toSafeDeals(row?.deals),
      tags: toSafeArray(row?.tags ?? row?.vibes),
      imageUrl: venue?.main_image_url || row?.image_url || row?.imageUrl || '/images/Auto_Main_Image.jpg',
      logoUrl: venue?.logo_url || '',
      mainImageUrl: venue?.main_image_url || '/images/Auto_Main_Image.jpg',
      galleryImages: toSafeArray(venue?.gallery_images),
      venueName: venue?.venue_name || row?.venue_name || row?.venueName || 'Venue',
      address: venue?.address || row?.address || '',
      streetAddress: venue?.street_address || row?.street_address || row?.streetAddress || '',
      city: venue?.city || row?.city || '',
      state: venue?.state || row?.state || '',
      zipCode: venue?.zip_code || row?.zip_code || row?.zipCode || '',
      lat,
      lng,
      website: venue?.website || row?.website || '',
      facebook: venue?.facebook || row?.facebook || '',
      instagram: venue?.instagram || row?.instagram || '',
      xHandle: venue?.x_handle || row?.x_handle || row?.xHandle || '',
      ownerId: null as any,
      venueId: row?.venue_id || venue?.id || undefined,
      listing_status: 'live' as any,
      isVerified: isVenueVerified(venue?.is_verified),
      is_verified: isVenueVerified(venue?.is_verified),
    } as any;
  }, []);

  // -----------------------------
  // ✅ Load PUBLIC specials for Landing/Results
  // -----------------------------
  const loadPublicSpecials = useCallback(async () => {
    try {
      const { data: specials, error: specialsError } = await supabase
        .from('specials')
        .select('*')
        .eq('is_published', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (specialsError) {
        console.error('[public specials] error:', specialsError);
        return;
      }

      const venueIds = Array.from(new Set((specials || []).map((s: any) => s.venue_id).filter(Boolean)));

      if (venueIds.length === 0) {
        console.log('[public] specials rows: 0, venues rows: 0, mapped events: 0');
        setPublicEvents([]);
        return;
      }

      const { data: venues, error: venuesError } = await supabase
        .from('Venues')
        .select(
          'id, venue_name, address, street_address, city, state, zip_code, latitude, longitude, website, facebook, instagram, x_handle, logo_url, main_image_url, gallery_images, is_active, listing_status, subscription_status, is_verified'
        )
        .in('id', venueIds)
        .eq('is_active', true);

      if (venuesError) {
        console.error('[public venues] error:', venuesError);
        return;
      }

      const normalizeStatus = (value: any) => String(value || '').trim().toLowerCase();
      const isPublicVenue = (venue: any) => {
        const verified = isVenueVerified(venue?.is_verified);
        const listing = normalizeStatus(venue?.listing_status);
        const subscription = normalizeStatus(venue?.subscription_status);

        if (verified) {
          return listing === 'active' && ['active', 'trialing'].includes(subscription);
        }

        return listing === 'active';
      };

      const venuesById = new Map<string, any>();
      (venues || [])
        .filter((v: any) => isPublicVenue(v))
        .forEach((v: any) => venuesById.set(v.id, v));

      const mapped = (specials || [])
        .filter((row: any) => venuesById.has(row.venue_id))
        .map((row: any) => mapPublicRowToEvent(row, venuesById.get(row.venue_id)))
        .sort(compareVerifiedFirst);

      console.log(
        `[public] specials rows: ${specials?.length ?? 0}, venues rows: ${venues?.length ?? 0}, mapped events: ${mapped.length}`
      );
      setPublicEvents(mapped);
    } catch (e) {
      console.error('[public specials] load failed:', e);
    }
  }, [mapPublicRowToEvent]);

  // Load public specials at startup, and whenever user returns to landing/results
  useEffect(() => {
    if (view === 'landing' || view === 'results') {
      loadPublicSpecials();
    }
  }, [view, loadPublicSpecials]);

  // Keep hash route in sync with current view so browser back/forward is stable
  useEffect(() => {
    if (!routeReady) return;

    let targetHash = '#/';

    if (view === 'results') {
      targetHash = '#/results';
    } else if (view === 'merchant-auth') {
      const currentHashQuery = window.location.hash.split('?')[1] || '';
      const currentParams = new URLSearchParams(currentHashQuery);
      const params = new URLSearchParams({ mode: authMode });
      const previewVenue = currentParams.get('previewVenue')?.trim();

      if (authMode === 'register' && previewVenue) {
        params.set('previewVenue', previewVenue);
      }
      
      targetHash = `#/merchant-auth?${params.toString()}`;
    } else if (view === 'merchant-portal') {
      targetHash = `#/venue-hub?tab=${merchantInitialTab}`;
    } else if (view === 'special-editor') {
      targetHash = '#/special-editor';
    } else if (view === 'venue-page') {
      targetHash = '#/venue';
    } else if (view === 'thank-you') {
      targetHash = '#/thank-you';
    } else if (view === 'privacy-policy') {
      targetHash = '#/privacy-policy';
    } else if (view === 'terms-of-service') {
      targetHash = '#/terms-of-service';
    }
  
    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    }
   }, [view, authMode, merchantInitialTab, routeReady]);

  // ✅ Pull pendingVenueId from localStorage (Stripe signup flow)
  useEffect(() => {
    const pendingId = (localStorage.getItem('pendingVenueId') || '').trim();
    if (pendingId) {
      setVenueUuid(pendingId);
    }

    const pendingName = (localStorage.getItem('pendingVenueName') || '').trim();
    if (pendingName) {
      setVenueIdentity((prev) => ({ ...prev, venueName: prev.venueName || pendingName }));
    }
  }, []);

  // ✅ Keep venue/subscription state fresh while logged in (for Stripe portal reactivation/cancel returns)
  useEffect(() => {
    if (!currentUserEmail) {
      setVenueUuid(null);
      setSubscriptionStatus(null);
      setListingStatus(null);
      return;
    }

    let cancelled = false;

    const refreshVenue = async () => {
      try {
        console.log('[Venues] Looking up venue by business_email:', currentUserEmail);

        const { data: rows, error } = await supabase
          .from('Venues')
          .select('*')
          .eq('business_email', currentUserEmail)
          .order('created_at', { ascending: false });

        if (cancelled) return;

        if (error) {
          console.error('[Venues] lookup error:', error);
          setVenueUuid(null);
          setSubscriptionStatus(null);
          setListingStatus(null);
          return;
        }

        const normalizeStatus = (value: any) => String(value || '').trim().toLowerCase();
        const pendingVenueId = (localStorage.getItem('pendingVenueId') || '').trim();

        const scoreVenue = (r: any) => {
          const sub = normalizeStatus(r?.subscription_status);
          const list = normalizeStatus(r?.listing_status);
          if (pendingVenueId && String(r?.id || '') === pendingVenueId) return 10;
          if (sub === 'active' || sub === 'trialing') return 4;
          if (list === 'active') return 3;
          if (r?.stripe_customer_id) return 2;
          return 1;
        };

        const data = Array.isArray(rows) && rows.length > 0
          ? [...rows].sort((a: any, b: any) => scoreVenue(b) - scoreVenue(a))[0]
          : null;
        
        const uuid = (data as any)?.id ?? null;
        console.log('[Venues] lookup result:', data, 'venueUuid:', uuid, 'rows found:', rows?.length || 0);

        const normalizedSubscriptionStatus = String((data as any)?.subscription_status || '').trim() || null;
        const normalizedListingStatus = String((data as any)?.listing_status || '').trim() || null;
        
        setVenueUuid(uuid);
        setSubscriptionStatus(normalizedSubscriptionStatus);
        setListingStatus(normalizedListingStatus);

        if (data) {
          setVenueIdentity((prev) => ({
            ...prev,
            venueName: (data as any).venue_name ?? prev.venueName,
            address: (data as any).address ?? prev.address,
            streetAddress: (data as any).street_address ?? prev.streetAddress,
            city: (data as any).city ?? prev.city,
            state: (data as any).state ?? prev.state,
            zipCode: (data as any).zip_code ?? prev.zipCode,
            website: (data as any).website ?? prev.website,
            facebook: (data as any).facebook ?? prev.facebook,
            instagram: (data as any).instagram ?? prev.instagram,
            xHandle: (data as any).x_handle ?? prev.xHandle,
            logoUrl: (data as any).logo_url ?? prev.logoUrl,
            mainImageUrl: (data as any).main_image_url ?? (prev.mainImageUrl || '/images/Auto_Main_Image.jpg'),
            galleryImages: (data as any).gallery_images ?? prev.galleryImages,
            isVerified: isVenueVerified((data as any).is_verified),
            is_verified: isVenueVerified((data as any).is_verified),
          }));
        }
      } catch (e) {
        console.error('[Venues] lookup failed:', e);
        setVenueUuid(null);
        setSubscriptionStatus(null);
        setListingStatus(null);
      }
    };

    refreshVenue();

    const onFocus = () => {
      refreshVenue();
      loadPublicSpecials();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshVenue();
        loadPublicSpecials();
      }
    };

    const refreshTimer = window.setInterval(() => {
      refreshVenue();
    }, 20000);

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(refreshTimer);
    };
  }, [currentUserEmail, loadPublicSpecials]);

  // ✅ When merchant portal opens, load merchant specials for this venue
  useEffect(() => {
    if (view !== 'merchant-portal') return;
    if (!venueUuid) return;
    if (!currentUserEmail) return;

    let cancelled = false;

    (async () => {
      try {
        console.log('[merchant specials] Loading specials for venue_id:', venueUuid);

        const { data, error } = await supabase
          .from('specials')
          .select('*')
          .eq('venue_id', venueUuid)
          .order('created_at', { ascending: false });

        if (cancelled) return;

        if (error) {
          console.error('[merchant specials] load error:', error);
          return;
        }

        const mapped: SpecialEvent[] = (data || []).map((row: any) => ({
          id: row.id,
          name: row.title,
          specialType: row.title,
          startTime: (row.start_time || '16:00').slice(0, 5),
          endTime: (row.end_time || '19:00').slice(0, 5),
          days: row.active_days || [],
          deals: toSafeDeals(row.deals),
          tags: toSafeArray(row.tags),
          imageUrl: venueIdentity.mainImageUrl || row.image_url || '',
          venueName: venueIdentity.venueName || row.venue_name || 'Venue',
          address: venueIdentity.address || '',
          streetAddress: venueIdentity.streetAddress || '',
          city: venueIdentity.city || '',
          state: venueIdentity.state || '',
          zipCode: venueIdentity.zipCode || '',
          website: venueIdentity.website || '',
          facebook: venueIdentity.facebook || '',
          instagram: venueIdentity.instagram || '',
          xHandle: venueIdentity.xHandle || '',
          logoUrl: venueIdentity.logoUrl || '',
          mainImageUrl: venueIdentity.mainImageUrl || '',
          galleryImages: venueIdentity.galleryImages || [],
          ownerId: currentUserEmail,
          venueId: venueUuid || undefined,
          listing_status: 'active' as any,
          isVerified: isVenueVerified(venueIdentity.isVerified ?? venueIdentity.is_verified),
          is_verified: isVenueVerified(venueIdentity.isVerified ?? venueIdentity.is_verified),
        })) as any;

        setMerchantEvents(mapped);
      } catch (e) {
        console.error('[merchant specials] load failed:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, venueUuid, currentUserEmail, venueIdentity]);

  useEffect(() => {
    if (currentUserEmail) return;
  }, [currentUserEmail]);

  const handleSearch = useCallback((params: SearchParams) => {
    setSearchParams(params);
    setView('results');
  }, []);

  const handleVenueClick = useCallback((venue: SpecialEvent) => {
    setSelectedVenue(venue);
    setView('venue-page');
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      setCurrentUserEmail(null);
      setPlan('partner' as SubscriptionPlan);
      setVenueIdentity(INITIAL_IDENTITY);
      setVenueUuid(null);
      setSubscriptionStatus(null);
      setPortalInitialTab('specials');
      setSelectedVenue(null);
      setEditingSpecial(null);
      setMerchantEvents([]);
      setView('landing');
      // leave publicEvents alone (still loads on landing)
    }
  }, [signOut]);

  const handleAuthSuccess = useCallback(
    (
      isNewUser: boolean,
      selectedPlan: SubscriptionPlan,
      credentials?: { email: string; venueName?: string }
    ) => {
      const email = credentials?.email || 'partner@demo.com';

      const vName =
        (credentials?.venueName && credentials.venueName.trim()) ||
        (localStorage.getItem('pendingVenueName') || '').trim() ||
        '';

      setCurrentUserEmail(email);
      setPlan(selectedPlan);

      if (isNewUser) {
        setVenueIdentity({ ...INITIAL_IDENTITY, venueName: vName });
        setPortalInitialTab('identity');
        setView('merchant-portal');
        return;
      }

      setPortalInitialTab('specials');
      setView('merchant-portal');
    },
    []
  );

  const handleAddEvent = useCallback((newEvent: SpecialEvent) => {
    setMerchantEvents((prev) => [newEvent, ...prev]);
    setEditingSpecial(null);
    setView('merchant-portal');
    // refresh public so new publish shows up for consumers (if published)
    loadPublicSpecials();
  }, [loadPublicSpecials]);

  const handleEditEvent = useCallback((updated: SpecialEvent) => {
    const updatedId = (updated as any)?.id;
    setMerchantEvents((prev) =>
      prev.map((e: any) => (e?.id === updatedId ? (updated as any) : e))
    );
    setEditingSpecial(null);
    setView('merchant-portal');
    loadPublicSpecials();
  }, [loadPublicSpecials]);

  const handleRemoveEvent = async (specialId: string) => {
    if (!specialId) return;

    try {
      const vId =
        (venueIdentity as any)?.id ||
        (venueUuid as any) ||
        null;

      const res = await fetch("/.netlify/functions/delete-special", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          special_id: specialId,
          venue_id: vId,
        }),
      });

      const raw = await res.text();
      let json: any = null;
      try {
        json = JSON.parse(raw);
      } catch {}

      if (!res.ok) {
        throw new Error(json?.error || raw || "Delete failed");
      }

      setMerchantEvents((prev) => prev.filter((e: any) => e.id !== specialId));
      loadPublicSpecials();
    } catch (err: any) {
      console.error("[delete-special] failed", err);
      alert(err?.message || "Could not delete. Please try again.");
    }
  };

  const handleUpdateIdentity = useCallback(
    (newIdentity: VenueIdentity) => {
      setVenueIdentity(newIdentity);

      // Keep merchant events updated visually
      setMerchantEvents((prev) =>
        prev.map((e: any) => {
          if (!currentUserEmail) return e;
          if (e?.ownerId !== currentUserEmail) return e;

          const next: any = {
            ...e,
            venueName: newIdentity.venueName,
            address: newIdentity.address,
            streetAddress: newIdentity.streetAddress,
            city: newIdentity.city,
            state: newIdentity.state,
            zipCode: newIdentity.zipCode,
            website: newIdentity.website,
            facebook: newIdentity.facebook,
            instagram: newIdentity.instagram,
            xHandle: newIdentity.xHandle,
            logoUrl: newIdentity.logoUrl,
            galleryImages: newIdentity.galleryImages,
          };

          const eventImage = e?.imageUrl ?? '';
          const identityHero = newIdentity.mainImageUrl ?? '';
          const currentMain = e?.mainImageUrl ?? identityHero;

          next.mainImageUrl = currentMain || identityHero;
          next.imageUrl = eventImage === '' || eventImage === currentMain ? next.mainImageUrl : eventImage;

          return next;
        })
      );
    },
    [currentUserEmail]
  );

  // ✅ Wrap SpecialEditor onSave so saved item always has ownerId and shows in merchant portal
  const handleSpecialSavedFromEditor = useCallback(
    (saved: Partial<SpecialEvent>) => {
      if (!currentUserEmail) return;

      const normalized: any = {
        ...saved,
        ownerId: currentUserEmail,
        venueId: venueUuid || (saved as any).venueId,
        venueName: venueIdentity.venueName || (saved as any).venueName || 'Venue',
        address: venueIdentity.address || '',
        city: venueIdentity.city || '',
        state: venueIdentity.state || '',
        zipCode: venueIdentity.zipCode || '',
      };

      if (editingSpecial) {
        handleEditEvent(normalized as SpecialEvent);
      } else {
        handleAddEvent(normalized as SpecialEvent);
      }
    },
    [currentUserEmail, venueUuid, venueIdentity, editingSpecial, handleAddEvent, handleEditEvent]
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFDF5] text-black selection:bg-amber-200">
      <Navbar
        onNavigate={(v) => navigateTo(v)}
        onAuthMode={(mode) => navigateToAuth(mode)}
        currentView={view}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        authMode={authMode}
      />

      <main className="flex-1 transition-all duration-500">
        {view === 'landing' && (
          <LandingPage
            events={publicEvents}
            onSearch={handleSearch}
            onBusinessClick={() => navigateToAuth('register')}
          />
        )}

        {view === 'results' && (
          <ResultsPage
            events={publicEvents}
            searchParams={searchParams}
            onBack={() => window.history.back()}
            onVenueSelect={handleVenueClick}
            onPreviewSignup={navigateToPreviewSignup}
          />
        )}

        {view === 'venue-page' && selectedVenue && (
          <VenuePage venue={selectedVenue} onBack={() => window.history.back()} />
        )}

        {view === 'merchant-auth' && (
          <MerchantAuth
            key={authMode}
            onAuthSuccess={handleAuthSuccess}
            initialLoginMode={authMode === 'login'}
          />
        )}

        {view === 'merchant-portal' && (
          <MerchantPortal
            events={merchantEvents.filter((e: any) => e?.ownerId === currentUserEmail)}
            identity={venueIdentity}
            venueId={venueUuid}
            onUpdateIdentity={handleUpdateIdentity}
            onRemoveEvent={handleRemoveEvent}
            onEditEvent={(e: SpecialEvent) => {
              setEditingSpecial(e);
              setView('special-editor');
            }}
            onAddNew={() => {
              setEditingSpecial(null);
              setView('special-editor');
            }}
            plan={plan}
            initialTab={merchantInitialTab}
            subscriptionStatus={subscriptionStatus}
            listingStatus={listingStatus}
          />
        )}

        {view === 'thank-you' && (
          <ThankYouSimple
            onContinue={() => {
              setSubscriptionStatus('trialing');
              setListingStatus('active');
              setMerchantInitialTab('identity');
              setView('merchant-portal');
              window.location.hash = '#/venue-hub?tab=identity';
            }}
          />
        )}

        {view === 'special-editor' && (
          <SpecialEditor
            onSave={handleSpecialSavedFromEditor}
            onCancel={() => setView('merchant-portal')}
            plan={plan}
            venueId={(venueIdentity as any).id || venueUuid || ''}
            venueName={venueIdentity.venueName || 'New Venue'}
            initialData={editingSpecial}
            venueHeroImage={venueIdentity.mainImageUrl}
          />
        )}

        {view === 'privacy-policy' && (
          <div className="max-w-4xl mx-auto px-6 py-40 animate-in fade-in duration-700 text-left">
            <h1 className="text-5xl md:text-7xl font-black serif tracking-tight mb-12">
              Privacy <span className="text-amber-600 italic">Policy.</span>
            </h1>
            <div className="space-y-8 text-lg font-medium text-gray-700 leading-relaxed Hyde">
              <p>
                At Hoppy Hours, your privacy is paramount. We are committed to protecting the
                personal information you share with us while searching for the best local deals.
              </p>
              <h3 className="text-2xl font-black serif text-black">Data Collection</h3>
              <p>
                We collect minimal data necessary to provide our services, including location data
                (if permitted) to show nearby happy hours, and email addresses for those who opt-in
                to notifications.
              </p>
              <h3 className="text-2xl font-black serif text-black">Third-Party Services</h3>
              <p>
                We use Google Gemini AI and Google Maps to enhance our search capabilities. These
                services process data according to their own privacy policies.
              </p>
              <h3 className="text-2xl font-black serif text-black">Your Rights</h3>
              <p>
                You have the right to request deletion of your data or opt-out of notifications at
                any time by contacting us at hello@hoppy-hours.com.
              </p>
            </div>
            <button
              onClick={() => navigateTo('landing')}
              className="mt-16 px-10 py-5 bg-black text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all text-center"
            >
              Back to Home
            </button>
          </div>
        )}

        {view === 'terms-of-service' && (
          <div className="max-w-4xl mx-auto px-6 py-40 animate-in fade-in duration-700 text-left">
            <h1 className="text-5xl md:text-7xl font-black serif tracking-tight mb-12">
              Terms of <span className="text-amber-600 italic">Service.</span>
            </h1>
            <div className="space-y-8 text-lg font-medium text-gray-700 leading-relaxed Hyde">
              <p>
                By using Hoppy Hours, you agree to the following terms and conditions. These terms
                govern your access to and use of our platform.
              </p>
              <h3 className="text-2xl font-black serif text-black">Accuracy of Information</h3>
              <p>
                While we strive for accuracy, happy hour deals are subject to change by venues
                without notice. Hoppy Hours is not responsible for expired or incorrect pricing.
              </p>
              <h3 className="text-2xl font-black serif text-black">Merchant Responsibility</h3>
              <p>
                Venues are responsible for the content they publish. We reserve the right to
                remove any listings that violate our community guidelines or local laws.
              </p>
              <h3 className="text-2xl font-black serif text-black">Limitation of Liability</h3>
              <p>
                Hoppy Hours is a discovery platform and is not liable for any issues arising from
                your visit to a listed venue.
              </p>
            </div>
            <button
              onClick={() => navigateTo('landing')}
              className="mt-16 px-10 py-5 bg-black text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all text-center"
            >
              Back to Home
            </button>
          </div>
        )}
      </main>

      <Footer onNavigate={(v) => navigateTo(v)} />
    </div>
  );
}
